import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useCustomisation } from "../../../context/CustomisationContext";
import { useTranslate } from "../../../hooks/useTranslate";
import { getShadeColour, isValidEmail } from "../../../utils/helpers";
import { MAX_LENGTH_EMAIL } from "../../../utils/inputLimits";
import { BackToLoginButton } from "../../components/BackToLoginButton";

interface SendEmailFormProps {
  sendEmail: () => void;
  loading: boolean;
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
}

export const SendEmailForm: React.FC<SendEmailFormProps> = ({
  sendEmail,
  loading,
  email,
  setEmail,
}) => {
  const [emailError, setEmailError] = useState<string>("");
  const { customisations } = useCustomisation();
  const t = useTranslate("forgot-password");
  const {
    LOGIN: {
      textColorsSettings: loginTextColors,
      inputFieldsSettings,
      buttonsSettings: loginbuttonssettings,
    },
    GPC: {
      textColorsSettings: gpcTextColors,
      buttonsSettings: gpcbuttonssettings,
    },
  } = customisations.customisations;
  const secondaryTextColor =
    loginTextColors?.loginSecondaryTextColor ??
    gpcTextColors?.gpcSecondaryTextColor;
  const secondaryButtonBg =
    loginbuttonssettings?.loginBtnSecondaryBgColor ??
    gpcbuttonssettings?.gpcBtnPrimaryBgColor;
  const secondaryButtonText =
    loginbuttonssettings?.loginBtnSecondaryTextColor ??
    gpcbuttonssettings?.gpcBtnSecondaryTextColor;

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailError("");
    setEmail(event.target.value);
  };

  const handleSendEmail = () => {
    if (!isValidEmail(email)) {
      setEmailError(t("forgotPasswordMessages", "emailInvalid"));
    } else {
      sendEmail();
    }
  };

  return (
    <>
      <Stack spacing={1} width="100%" alignItems="center">
        <Typography variant="h5" color={secondaryTextColor} textAlign="center">
          {t("forgotPasswordHeader", "forgotPasswordText")}
        </Typography>
        <Typography
          variant="body1"
          color={secondaryTextColor}
          textAlign="center"
        >
          {t("forgotPasswordHeader", "forgotPasswordDsc")}
        </Typography>
      </Stack>
      <Stack spacing={2} width="100%">
        <Stack spacing={1}>
          <Typography variant="subtitle2" color={secondaryTextColor}>
            {t("forgotPasswordForm", "email")}
          </Typography>
          <TextField
            fullWidth
            id="email"
            error={Boolean(emailError)}
            helperText={emailError}
            placeholder={t("forgotPasswordForm", "emailPlaceholder")}
            type="email"
            value={email}
            onChange={handleEmailChange}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !loading) {
                handleSendEmail();
              }
            }}
            variant="outlined"
            size="small"
            slotProps={{
              input: {
                sx: {
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: inputFieldsSettings?.inputBorderColor,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: inputFieldsSettings?.inputBorderColor,
                  },
                  backgroundColor: inputFieldsSettings?.inputBGColor,
                },
                endAdornment: emailError && (
                  <ErrorOutlineIcon color="error" fontSize="small" />
                ),
              },
              htmlInput: { maxLength: MAX_LENGTH_EMAIL },
            }}
          />
        </Stack>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Button
            fullWidth
            sx={{
              py: 1.5,
              borderRadius: "12px",
              backgroundColor: `${secondaryButtonBg} !important`,
              color: `${secondaryButtonText}`,
              "&:hover": {
                bgcolor: `${getShadeColour(secondaryButtonBg!, -8)} !important`,
                fontWeight: 700,
              },
            }}
            onClick={handleSendEmail}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              {t("forgotPasswordForm", "btnReset")}
            </Typography>
          </Button>
        )}
      </Stack>
      <BackToLoginButton />
    </>
  );
};
